import React from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';

import { ClickAwayListener, IconButton } from '@mui/material';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { StyledTextField } from 'components/Input/styles';
import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue';

import { useStore } from 'utils/hooks/store';
import { EntityValueType, DocumentAccessLevel } from 'utils/api/modules/entities/entities.types';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { ProtectorType } from 'core/constants';

import PermissionBadge from 'containers/DocumentsDetails/DocumentPermissions/PermissionBadge';

import {
  HeaderContainer,
  TitleContainer,
  TitleText,
  TitleActions,
  MetadataRow,
  MetadataLabel,
  MetadataValue,
} from './styles';
import InlineEditableField from './InlineEditableField';

const EXCLUDED_FIELDS = new Set(['documents__name']);

export const DocumentHeader: React.FC = observer(() => {
  const params = useParams<{ shareId: string }>();
  const { documentsDetails, entityFields, session } = useStore();

  const editable = !params.shareId;
  const canEdit = editable && !session?.user?.readOnly && documentsDetails.accessLevel !== DocumentAccessLevel.View;
  const { details } = documentsDetails;
  const title = details ? documentsDetails.getName(details) : '';
  const createdAt = details?.createdAt ?? null;
  const createdBy = details?.createdBy ?? null;

  const additionalFields = React.useMemo(() => {
    if (!details?.layout) {
      return [];
    }

    const fields: string[] = [];
    details.layout.order.forEach((sectionKey) => {
      const section = details.layout.properties[sectionKey];
      if (section?.order) {
        section.order.forEach((fieldKey) => {
          if (!EXCLUDED_FIELDS.has(fieldKey)) {
            fields.push(fieldKey);
          }
        });
      }
    });
    return fields;
  }, [details?.layout]);

  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [localTitle, setLocalTitle] = React.useState(title);

  React.useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  const handleSaveTitle = async (): Promise<void> => {
    if (localTitle.trim() && localTitle !== title) {
      const nameKey = `${documentsDetails.entityType}__name`;
      await documentsDetails.edit({ [nameKey]: localTitle.trim() });
    }
    setIsEditingTitle(false);
  };

  const handleDiscardChanges = (): void => {
    setLocalTitle(title);
    setIsEditingTitle(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    }
    if (e.key === 'Escape') {
      handleDiscardChanges();
    }
  };

  const handleSaveField = async (fieldKey: string, value: EntityValueType): Promise<boolean> =>
    documentsDetails.edit({ [fieldKey]: value });

  return (
    <HeaderContainer>
      <TitleContainer>
        {isEditingTitle ? (
          <ClickAwayListener onClickAway={handleDiscardChanges}>
            <StyledTextField
              fullWidth
              size="small"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Untitled document"
              autoFocus
              inputProps={{
                style: { fontSize: '32px', fontWeight: 700 },
              }}
            />
          </ClickAwayListener>
        ) : (
          <TitleText
            onClick={() => canEdit && setIsEditingTitle(true)}
            $editable={canEdit}
            data-testid="document-title"
          >
            {title || 'Untitled document'}
          </TitleText>
        )}
        {isEditingTitle && (
          <TitleActions>
            <IconButton onClick={handleDiscardChanges} size="small">
              <CloseIcon sx={{ width: '24px', height: '24px', color: 'action.active' }} />
            </IconButton>
            <IconButton onClick={handleSaveTitle} size="small">
              <CheckOutlinedIcon sx={{ width: '24px', height: '24px', color: 'action.active' }} />
            </IconButton>
          </TitleActions>
        )}
        {!params.shareId && (
          <PermissionBadge accessLevel={documentsDetails.accessLevel} isOwner={documentsDetails.isOwner} />
        )}
      </TitleContainer>

      <MetadataRow>
        <MetadataLabel>Created</MetadataLabel>
        <MetadataValue>
          <CustomFieldDisplayValue type={ProtectorType.DateTime} value={createdAt as AnswerTypes} />
        </MetadataValue>
      </MetadataRow>

      <MetadataRow>
        <MetadataLabel>Created by</MetadataLabel>
        <MetadataValue>
          <CustomFieldDisplayValue type={ProtectorType.User} value={createdBy as AnswerTypes} />
        </MetadataValue>
      </MetadataRow>

      {additionalFields.map((fieldKey) => {
        const entityValue = details?.entity[fieldKey];
        const field = entityFields.getFieldById(fieldKey, entityValue?.value);

        if (!field) {
          return null;
        }

        return (
          <InlineEditableField
            key={fieldKey}
            fieldKey={fieldKey}
            schema={field.schema}
            value={field.value}
            canEdit={canEdit}
            onSave={handleSaveField}
          />
        );
      })}
    </HeaderContainer>
  );
});
